import React, { useState, useEffect } from 'react';
import {
  Box, Typography, TextField, Button, Card, Grid, MenuItem, CircularProgress,
  FormControl, InputLabel, Select, Snackbar, Alert, IconButton, Autocomplete
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddTaskIcon from '@mui/icons-material/AddTask';
import PersonIcon from '@mui/icons-material/Person';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import DescriptionIcon from '@mui/icons-material/Description';
import ApiService from '../../../../services/api';
import logger from '../../../../utils/logger';

const Container = styled(Box)(({ theme }) => ({
  padding: '24px',
  backgroundColor: '#f5f7fa',
  minHeight: '100vh',
}));

const TitleSection = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  marginBottom: '32px',
}));

const TitleIcon = styled(Box)(({ theme }) => ({
  width: '48px',
  height: '48px',
  borderRadius: '12px',
  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: '#fff',
  boxShadow: '0 4px 14px rgba(16, 185, 129, 0.4)',
}));

const FormCard = styled(Card)(({ theme }) => ({
  maxWidth: '800px',
  margin: '0 auto',
  padding: '32px',
  borderRadius: '20px',
  boxShadow: '0 4px 25px rgba(0,0,0,0.05)',
  border: '1px solid #eef0f3',
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontSize: '16px',
  fontWeight: 700,
  color: '#10b981',
  marginBottom: '20px',
  marginTop: '24px',
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  '&:first-of-type': { marginTop: 0 },
  '&::after': {
    content: '""',
    flex: 1,
    height: '1px',
    background: 'linear-gradient(to right, #d1fae5, transparent)',
  },
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    backgroundColor: '#fcfcfd',
    '&:hover fieldset': { borderColor: '#10b981' },
    '&.Mui-focused fieldset': { borderColor: '#10b981' },
  },
  '& .MuiInputLabel-root.Mui-focused': { color: '#10b981' },
}));

const StyledSelect = styled(FormControl)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    backgroundColor: '#fcfcfd',
    '&:hover fieldset': { borderColor: '#10b981' },
    '&.Mui-focused fieldset': { borderColor: '#10b981' },
  },
  '& .MuiInputLabel-root.Mui-focused': { color: '#10b981' },
}));

const SubmitButton = styled(Button)(({ theme }) => ({
  padding: '12px 40px',
  fontWeight: 700,
  textTransform: 'none',
  fontSize: '16px',
  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
  borderRadius: '12px',
  boxShadow: '0 4px 14px rgba(16, 185, 129, 0.4)',
  '&:hover': {
    background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
  },
}));

const AddFollowup = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [leads, setLeads] = useState([]);
  const [salesEmployees, setSalesEmployees] = useState([]);
  const [loadingLeads, setLoadingLeads] = useState(true);
  const [loadingEmployees, setLoadingEmployees] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const [form, setForm] = useState({
    lead_id: null,
    assigned_to_employee_id: '',
    title: '',
    description: '',
    followup_type: 'call',
    scheduled_date: '',
    scheduled_time: '',
    due_date: '',
    priority: 'normal',
  });

  useEffect(() => {
    fetchLeads();
    fetchSalesEmployees();
  }, []);

  const fetchLeads = async () => {
    try {
      setLoadingLeads(true);
      const response = await ApiService.get('/admin/leads/get-leads?pageSize=1000');
      const data = response?.data?.data || response?.data || response;
      setLeads(data.leads || []);
    } catch (error) {
      logger.error('Error fetching leads:', error);
    } finally {
      setLoadingLeads(false);
    }
  };

  const fetchSalesEmployees = async () => {
    try {
      setLoadingEmployees(true);
      const response = await ApiService.get('/admin/employee/get-telly-employees');
      const data = response?.data?.data || response?.data || response;
      setSalesEmployees(Array.isArray(data) ? data : []);
    } catch (error) {
      logger.error('Error fetching sales employees:', error);
      // Fallback: get all employees and filter by designation
      try {
        const allResp = await ApiService.get('/admin/employee/get-employees?pageSize=100');
        const allData = allResp?.data?.data || allResp?.data || allResp;
        const employees = allData.employees || [];
        const salesList = employees.filter(e =>
          e.designation?.toLowerCase().includes('inside') ||
          e.designation?.toLowerCase().includes('cro') ||
          e.department?.toLowerCase().includes('tele') ||
          e.department?.toLowerCase().includes('sales')
        );
        setSalesEmployees(salesList.length > 0 ? salesList : employees);
      } catch (fallbackError) {
        logger.error('Fallback error:', fallbackError);
      }
    } finally {
      setLoadingEmployees(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleLeadChange = (event, newValue) => {
    setForm((prev) => ({
      ...prev,
      lead_id: newValue,
      title: newValue ? `Follow-up: ${newValue.business_name}` : prev.title,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.assigned_to_employee_id) {
      setSnackbar({ open: true, message: 'Please select a sales team member', severity: 'error' });
      return;
    }
    if (!form.title) {
      setSnackbar({ open: true, message: 'Please enter a title', severity: 'error' });
      return;
    }

    setLoading(true);
    try {
      const payload = {
        lead_id: form.lead_id?.id || null,
        assigned_to_employee_id: parseInt(form.assigned_to_employee_id),
        title: form.title,
        description: form.description || null,
        followup_type: form.followup_type,
        scheduled_date: form.scheduled_date || null,
        scheduled_time: form.scheduled_time || null,
        due_date: form.due_date || null,
        priority: form.priority,
      };

      await ApiService.post('/admin/followups', payload);
      setSnackbar({ open: true, message: 'Follow-up created successfully!', severity: 'success' });
      setTimeout(() => navigate('/admin-dashboard/followups'), 1500);
    } catch (error) {
      logger.error('Error creating followup:', error);
      setSnackbar({ open: true, message: error.response?.data?.message || 'Failed to create follow-up', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container>
      <TitleSection>
        <IconButton onClick={() => navigate('/admin-dashboard/followups')} sx={{ mr: 1 }}>
          <ArrowBackIcon />
        </IconButton>
        <TitleIcon>
          <AddTaskIcon sx={{ fontSize: 24 }} />
        </TitleIcon>
        <Box>
          <Typography variant="h4" fontWeight={700} color="#1f2937">Add Follow-up</Typography>
          <Typography variant="body2" color="text.secondary">Assign a new follow-up task to your sales team</Typography>
        </Box>
      </TitleSection>

      <FormCard>
        <form onSubmit={handleSubmit}>
          <SectionTitle>
            <PersonIcon fontSize="small" /> Assignment Details
          </SectionTitle>

          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Autocomplete
                options={leads}
                getOptionLabel={(option) => `${option.business_name || ''} - ${option.contact_person || ''}`}
                value={form.lead_id}
                onChange={handleLeadChange}
                loading={loadingLeads}
                renderInput={(params) => (
                  <StyledTextField
                    {...params}
                    label="Select Lead/Client (Optional)"
                    placeholder="Search leads..."
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingLeads && <CircularProgress size={20} />}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <StyledSelect fullWidth>
                <InputLabel>Assign To (Sales Team) *</InputLabel>
                <Select
                  name="assigned_to_employee_id"
                  value={form.assigned_to_employee_id}
                  onChange={handleChange}
                  label="Assign To (Sales Team) *"
                >
                  {loadingEmployees ? (
                    <MenuItem disabled>Loading...</MenuItem>
                  ) : salesEmployees.length === 0 ? (
                    <MenuItem disabled>No sales employees found</MenuItem>
                  ) : (
                    salesEmployees.map((emp) => (
                      <MenuItem key={emp.id} value={emp.id}>
                        {emp.first_name} {emp.last_name || ''} - {emp.designation || emp.department || 'Sales'}
                      </MenuItem>
                    ))
                  )}
                </Select>
              </StyledSelect>
            </Grid>

            <Grid item xs={12} sm={6}>
              <StyledSelect fullWidth>
                <InputLabel>Priority</InputLabel>
                <Select name="priority" value={form.priority} onChange={handleChange} label="Priority">
                  <MenuItem value="high">High</MenuItem>
                  <MenuItem value="normal">Normal</MenuItem>
                  <MenuItem value="low">Low</MenuItem>
                </Select>
              </StyledSelect>
            </Grid>
          </Grid>

          <SectionTitle>
            <DescriptionIcon fontSize="small" /> Follow-up Details
          </SectionTitle>

          <Grid container spacing={3}>
            <Grid item xs={12}>
              <StyledTextField
                fullWidth
                required
                name="title"
                label="Title"
                value={form.title}
                onChange={handleChange}
                placeholder="e.g., Welcome Call, Follow-up Call"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <StyledSelect fullWidth>
                <InputLabel>Follow-up Type</InputLabel>
                <Select name="followup_type" value={form.followup_type} onChange={handleChange} label="Follow-up Type">
                  <MenuItem value="call">Phone Call</MenuItem>
                  <MenuItem value="email">Email</MenuItem>
                  <MenuItem value="whatsapp">WhatsApp</MenuItem>
                  <MenuItem value="welcome_call">Welcome Call</MenuItem>
                  <MenuItem value="renewal">Renewal Reminder</MenuItem>
                  <MenuItem value="other">Other</MenuItem>
                </Select>
              </StyledSelect>
            </Grid>

            <Grid item xs={12}>
              <StyledTextField
                fullWidth
                multiline
                rows={3}
                name="description"
                label="Description"
                value={form.description}
                onChange={handleChange}
                placeholder="Add any notes or instructions for this follow-up..."
              />
            </Grid>
          </Grid>

          <SectionTitle>
            <CalendarTodayIcon fontSize="small" /> Schedule
          </SectionTitle>

          <Grid container spacing={3}>
            <Grid item xs={12} sm={4}>
              <StyledTextField
                fullWidth
                type="date"
                name="scheduled_date"
                label="Scheduled Date"
                value={form.scheduled_date}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <StyledTextField
                fullWidth
                type="time"
                name="scheduled_time"
                label="Scheduled Time"
                value={form.scheduled_time}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <StyledTextField
                fullWidth
                type="date"
                name="due_date"
                label="Due Date"
                value={form.due_date}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 4 }}>
            <Button variant="outlined" onClick={() => navigate('/admin-dashboard/followups')} sx={{ borderRadius: '12px', textTransform: 'none' }}>
              Cancel
            </Button>
            <SubmitButton variant="contained" type="submit" disabled={loading}>
              {loading ? <CircularProgress size={24} color="inherit" /> : 'Create Follow-up'}
            </SubmitButton>
          </Box>
        </form>
      </FormCard>

      <Snackbar open={snackbar.open} autoHideDuration={5000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>{snackbar.message}</Alert>
      </Snackbar>
    </Container>
  );
};

export default AddFollowup;
